import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Space, Tag, Row, Col, Popconfirm, Tooltip, Dropdown } from 'antd'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import Button from '../../../../../core/components/Button.jsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { ContactBadges } from '../../../../../core/components/ContactBadges.jsx'
import LabelDateBadge from '../../../../../core/components/LabelDateBadge.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faEdit, faCheck, faTrash, faEllipsisV, faLink } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { deleteInteraction } from '../../../../interactions/services/interactionsService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { TaskForm } from '../../../../tasks/forms/TaskForm.tsx'
import { InteractionForm, type InteractionType } from '../../../../interactions/forms/InteractionForm.tsx'
import { InteractionRecordFields } from '../../../../../state_manager/data_models/interaction_record.ts'
import { TaskRecordFields } from '../../../../../state_manager/data_models/task_record.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import {
  CONFIRM_DELETE_INTERACTION_TITLE,
  CONFIRM_DELETE_INTERACTION_DESCRIPTION,
  CONFIRM_DELETE_TASK_TITLE,
  CONFIRM_DELETE_TASK_DESCRIPTION
} from '../../../../../state_manager/constants/strings.ts'
import { deleteTask as deleteTaskApi } from '../../../../tasks/services/tasksService.ts'
import { LinkDelinkModal } from '../../../../interactions/components/LinkDelinkModal.jsx'
import { TaskLinkDelinkModal } from '../../../../tasks/components/TaskLinkDelinkModal.jsx'
import { ShadowRichHtml } from '../../../../../core/components/ShadowRichHtml.tsx'

const { Text } = Typography

/** What the detail panel is showing. */
export type DetailPanelItemType = 'task' | 'interaction'
/** How the detail panel is showing it. */
export type DetailPanelMode = 'view' | 'add-task' | 'add-interaction' | 'edit-task' | 'edit-interaction'

interface DetailPanelProps {
  selectedItem?: any
  /** null when nothing is selected yet — the callers keep it in a `useState<DetailPanelItemType | null>` */
  itemType?: DetailPanelItemType | null
  mode?: DetailPanelMode
  onClose?: () => void
  onEdit?: (item: any, itemType: string) => void
  onSubmit?: (data: any, mode: string) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  onDelink?: (id: string) => Promise<void>
  onRefreshInteractions?: () => Promise<void>
  onRefreshTasks?: () => Promise<void>
}

export function DetailPanel({
  selectedItem,
  itemType = 'task',
  mode = 'view',
  onClose,
  onEdit,
  onSubmit,
  onDelete,
  onRefreshInteractions,
  onRefreshTasks
}: DetailPanelProps) {
  const [interactionType, setInteractionType] = useState<InteractionType>('note')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [selectedContacts, setSelectedContacts] = useState<any[]>([])
  const [linkDelinkModalVisible, setLinkDelinkModalVisible] = useState<boolean>(false)
  const [taskLinkDelinkModalVisible, setTaskLinkDelinkModalVisible] = useState<boolean>(false)
  const [deletePopconfirmVisible, setDeletePopconfirmVisible] = useState<boolean>(false)
  const [taskDeletePopconfirmVisible, setTaskDeletePopconfirmVisible] = useState<boolean>(false)
  const ellipsesButtonRef = useRef<any>(null)
  const taskEllipsesButtonRef = useRef<any>(null)

  useEffect(() => {
    setDeletePopconfirmVisible(false)
    setTaskDeletePopconfirmVisible(false)
  }, [mode, itemType, selectedItem])

  const taskFormRef = useRef<any>(null)
  const interactionFormRef = useRef<any>(null)

  const navigate = useNavigate()

  const selectedTaskContacts = React.useMemo(() => {
    if (selectedItem && itemType === 'task') {
      const assigneesField = TaskRecordFields.prog__task_assignees
      const assigneesData = selectedItem?.[assigneesField] || []

      if (assigneesData.length === 0) {
        return []
      }

      return assigneesData
        .map((assignee: any) => ({
          label: assignee[UserEntryFields.prog__display_name] || '',
          value: assignee[UserEntryFields.contacts__id] || null,
          id: assignee[UserEntryFields.contacts__id] || null
        }))
        .filter((contact: any) => contact.label)
    }

    return []
  }, [selectedItem, itemType])

  const selectedInteractionOwner = React.useMemo(() => {
    if (selectedItem && itemType === 'interaction') {
      const ownerField = InteractionRecordFields.interactions__owner
      const ownerData = selectedItem?.[ownerField]

      if (!ownerData) {
        return []
      }

      const owner = {
        label: ownerData[UserEntryFields.prog__display_name] || '',
        value: ownerData[UserEntryFields.contacts__id] || null,
        id: ownerData[UserEntryFields.contacts__id] || null
      }

      return owner.label ? [owner] : []
    }
    return []
  }, [selectedItem, itemType])

  const selectedInteractionContacts = React.useMemo(() => {
    if (selectedItem && itemType === 'interaction') {
      const participantsField = InteractionRecordFields.prog__interaction_participants
      const participantsData = selectedItem?.[participantsField] || []

      if (participantsData.length === 0) {
        return []
      }

      return participantsData
        .map((participant: any) => ({
          label: participant[UserEntryFields.prog__display_name] || '',
          value: participant[UserEntryFields.contacts__id] || null,
          id: participant[UserEntryFields.contacts__id] || null
        }))
        .filter((contact: any) => contact.label)
    }
    return []
  }, [selectedItem, itemType])

  const handleContactClick = (contact: any, contactId: string | number) => {
    if (contactId) {
      navigate(`/contacts/forms/full/${contactId}`)
    }
  }

  const isFormMode = mode !== 'view'
  const isAddMode = mode.startsWith('add')
  const isTaskMode = itemType === 'task' || mode.includes('task')

  useEffect(() => {
    if (isFormMode) {
      if (isAddMode) {
        if (isTaskMode) {
          setSelectedContacts([])
          setInteractionType('note')
        } else {
          setInteractionType('note')
        }
      } else if (selectedItem) {
        if (isTaskMode) {
          const assigneesField = TaskRecordFields.prog__task_assignees
          const assigneesData = selectedItem?.[assigneesField] || []
          const contacts = assigneesData.map((assignee: any) => ({
            label: assignee[UserEntryFields.prog__display_name] || '',
            value: assignee[UserEntryFields.contacts__id] || '',
            email: assignee[UserEntryFields.contacts__email] || ''
          }))
          setSelectedContacts(contacts)
        } else {
          setSelectedContacts([])
        }

        if (!isTaskMode) {
          const typeField = InteractionRecordFields.interactions__type
          setInteractionType(selectedItem?.[typeField] || 'note')
        }
      }
    }
  }, [mode, selectedItem, isFormMode, isAddMode, isTaskMode])

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      const formRef = isTaskMode ? taskFormRef : interactionFormRef

      let values: any
      if (isTaskMode) {
        values = await formRef.current?.validateFields()
      } else {
        const isValid = formRef.current?.validateFields()
        if (!isValid) {
          return
        }
        values = formRef.current?.getValues()
      }

      let formattedData = { ...values }

      if (!isAddMode && selectedItem) {
        formattedData[isTaskMode ? TaskRecordFields.tasks__id : InteractionRecordFields.interactions__id] = isTaskMode
          ? selectedItem[TaskRecordFields.tasks__id]
          : selectedItem[InteractionRecordFields.interactions__id]
      }

      await onSubmit?.(formattedData, mode)
      onClose?.()
    } catch (error) {
      console.error('Form submission failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to save')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    const formRef = isTaskMode ? taskFormRef : interactionFormRef
    formRef.current?.resetFields()
    setInteractionType('note')
    setSelectedContacts([])
    onClose?.()
  }

  const handleDeleteTask = async () => {
    if (!selectedItem || itemType !== 'task') {
      return
    }

    const taskId = selectedItem[TaskRecordFields.tasks__id]
    if (!taskId) {
      showErrorToast('Unable to delete: Task ID not found')
      return
    }

    if (onDelete) {
      try {
        await onDelete(taskId)
        toast.success('Task deleted successfully')
        await onRefreshTasks?.()
      } catch (error) {
        console.error('Error deleting task:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to delete task')
      }
    } else {
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteTaskApi('', taskId, currentToken)
        toast.success('Task deleted successfully')
        onClose?.()
        await onRefreshTasks?.()
      } catch (error) {
        console.error('Error deleting task:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to delete task')
      }
    }
  }

  const handleDeleteInteraction = async () => {
    if (!selectedItem || itemType !== 'interaction') {
      return
    }

    const interactionId = selectedItem[InteractionRecordFields.interactions__id]
    if (!interactionId) {
      showErrorToast('Unable to delete: Interaction ID not found')
      return
    }

    if (onDelete) {
      try {
        await onDelete(interactionId)
        toast.success('Interaction deleted successfully')
      } catch (error) {
        console.error('Error deleting interaction:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to delete interaction')
      }
    } else {
      try {
        const currentToken = getAccessToken(store.getState().auth)
        await deleteInteraction('', interactionId, currentToken)
        toast.success('Interaction deleted successfully')
        onClose?.()
        await onRefreshInteractions?.()
      } catch (error) {
        console.error('Error deleting interaction:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to delete interaction')
      }
    }
  }

  if (!selectedItem && mode === 'view') return null

  const renderTaskDetail = (task: any) => {
    return (
      <div className='space-y-2 h-full'>
        <div className='h-full'>
          <div className='space-y-2 text-sm h-full'>
            <div className='font-semibold text-base mb-2'>{task[TaskRecordFields.tasks__subject] || 'No subject'}</div>
            <Row gutter={16} className='mb-3'>
              <Col span={20}>
                <div className='flex items-center gap-2'>
                  <Text strong className='text-xs whitespace-nowrap'>
                    Assigned to:
                  </Text>
                  <ContactBadges contacts={selectedTaskContacts} onContactClick={handleContactClick} />
                </div>
              </Col>
              <Col span={4}>
                <div className='flex justify-end items-center gap-2'>
                  <Text strong className='text-xs whitespace-nowrap'>
                    Due:
                  </Text>
                  {task[TaskRecordFields.tasks__due_by] ? (
                    <LabelDateBadge date={task[TaskRecordFields.tasks__due_by]} size='default' />
                  ) : (
                    '-'
                  )}
                </div>
              </Col>
            </Row>
            {task[TaskRecordFields.tasks__notes] && (
              <div className='bg-gray-100 p-2.5 rounded h-full'>
                <Text strong>Notes:</Text>
                <ShadowRichHtml className='mt-1' html={task[TaskRecordFields.tasks__notes]} />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderInteractionDetail = (interaction: any) => {
    const getTypeColor = (type: string): string => {
      switch (type) {
        case 'meeting':
          return 'blue'
        case 'email':
          return 'orange'
        case 'note':
          return 'green'
        default:
          return 'default'
      }
    }

    const subjectField = InteractionRecordFields.interactions__subject
    const typeField = InteractionRecordFields.interactions__type
    const datetimeField = InteractionRecordFields.interactions__datetime
    const descriptionField = InteractionRecordFields.interactions__description
    const notesField = InteractionRecordFields.interactions__notes

    const datetimeValue = interaction[datetimeField]
    const formattedDateTime =
      datetimeValue && dayjs(datetimeValue).isValid() ? dayjs(datetimeValue).format('MM/DD/YYYY HH:mm') : null

    return (
      <div className='space-y-2'>
        <div>
          <div className='space-y-2 text-sm'>
            <div>
              <div className='font-semibold text-base mb-2'>{interaction[subjectField] || 'No subject'}</div>
            </div>
            <Row gutter={12} justify='space-between' className='mb-3'>
              <Col span={6}>
                <div className='space-y-2'>
                  <div className='flex justify-start items-center gap-2'>
                    <Text strong className='text-xs whitespace-nowrap' style={{ width: '85px', minWidth: '85px' }}>
                      Type:
                    </Text>
                    <Tag color={getTypeColor(interaction[typeField])} className='mb-1 capitalize'>
                      {interaction[typeField]}
                    </Tag>
                  </div>
                </div>
              </Col>
              <Col span={18}>
                <div className='flex justify-end items-center gap-2'>
                  <div className='flex items-center gap-2'>
                    <Text strong className='text-xs whitespace-nowrap'>
                      Date:
                    </Text>
                    {formattedDateTime && (
                      <div
                        style={{
                          fontSize: '10px',
                          paddingLeft: 2,
                          paddingRight: 2,
                          paddingTop: 2,
                          paddingBottom: 2
                        }}
                        className='bg-blue-100 text-blue-600 border border-blue-300 py-0.5 h-5 rounded-md font-medium flex items-center justify-center'
                      >
                        {formattedDateTime}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={23}>
                {selectedInteractionOwner.length > 0 && (
                  <div className='flex items-center gap-2 mb-2'>
                    <Text strong className='text-xs whitespace-nowrap' style={{ width: '85px', minWidth: '85px' }}>
                      Owner:
                    </Text>
                    <ContactBadges contacts={selectedInteractionOwner} onContactClick={handleContactClick} />
                  </div>
                )}
                <div className='flex items-center gap-2'>
                  <Text strong className='text-xs whitespace-nowrap' style={{ width: '85px', minWidth: '85px' }}>
                    Participants:
                  </Text>
                  <ContactBadges contacts={selectedInteractionContacts} onContactClick={handleContactClick} />
                </div>
              </Col>
            </Row>
            {interaction[descriptionField] ? (
              <div className='bg-gray-100 p-2.5 rounded mb-3'>
                <Text strong>Description:</Text>
                <div className='mt-1'>{interaction[descriptionField]}</div>
              </div>
            ) : interaction[InteractionRecordFields.prog__summary_status] === 'pending' ||
              interaction[InteractionRecordFields.prog__summary_status] === 'processing' ? (
              // The summarizer queue has this interaction; the description
              // lands on the next tick (interaction summaries, 20261204120100).
              <div className='bg-gray-100 p-2.5 rounded mb-3'>
                <Text strong>Description:</Text>
                <div className='mt-1'>
                  <Text type='secondary' italic>
                    Summary pending…
                  </Text>
                </div>
              </div>
            ) : null}
            {interaction[notesField] && (
              <div className='bg-gray-100 p-2.5 rounded'>
                <Text strong>Notes:</Text>
                <ShadowRichHtml className='mt-1' html={interaction[notesField]} />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const getTaskInitialValues = (): any => {
    if (isAddMode) {
      return {
        assignedTo: [],
        dueDate: null,
        completed: false,
        notes: '',
        subject: ''
      }
    }

    const assigneesField = TaskRecordFields.prog__task_assignees
    const assigneesData = selectedItem?.[assigneesField] || []
    const assignedTo = assigneesData.map((assignee: any) => ({
      label: assignee[UserEntryFields.prog__display_name] || '',
      value: assignee[UserEntryFields.contacts__id] || '',
      email: assignee[UserEntryFields.contacts__email] || ''
    }))

    return {
      assignedTo: assignedTo,
      dueDate: selectedItem?.[TaskRecordFields.tasks__due_by]
        ? dayjs(selectedItem[TaskRecordFields.tasks__due_by])
        : null,
      completed: selectedItem?.[TaskRecordFields.tasks__completed] || false,
      notes: selectedItem?.[TaskRecordFields.tasks__notes] || '',
      subject: selectedItem?.[TaskRecordFields.tasks__subject] || ''
    }
  }

  const renderTaskForm = () => (
    <TaskForm
      ref={taskFormRef}
      initialValues={getTaskInitialValues()}
      selectedContacts={selectedContacts}
      onContactsChange={setSelectedContacts}
      className='detail-panel-form'
    />
  )

  const getInteractionInitialValues = (): any => {
    if (isAddMode) {
      return {
        type: 'note',
        datetime: `${dayjs().format('YYYY-MM-DD')} ${dayjs().format('HH:mm:ss')}`,
        subject: '',
        description: '',
        owner: null,
        participants: [],
        notes: ''
      }
    }

    const subjectField = InteractionRecordFields.interactions__subject
    const typeField = InteractionRecordFields.interactions__type
    const datetimeField = InteractionRecordFields.interactions__datetime
    const descriptionField = InteractionRecordFields.interactions__description
    const notesField = InteractionRecordFields.interactions__notes
    const ownerField = InteractionRecordFields.interactions__owner
    const participantsField = InteractionRecordFields.prog__interaction_participants

    let datetime: string | null = null
    const datetimeValue = selectedItem?.[datetimeField]

    if (datetimeValue) {
      const parsed = dayjs(datetimeValue)
      if (parsed.isValid()) {
        datetime = parsed.format('YYYY-MM-DD HH:mm:ss')
      }
    }

    if (!datetime) {
      datetime = `${dayjs().format('YYYY-MM-DD')} ${dayjs().format('HH:mm:ss')}`
    }

    const ownerData = selectedItem?.[ownerField]
    const owner = ownerData
      ? {
          label: ownerData[UserEntryFields.prog__display_name] || '',
          value: ownerData[UserEntryFields.contacts__id] || '',
          email: ownerData[UserEntryFields.contacts__email] || ''
        }
      : null

    const participantsData = selectedItem?.[participantsField] || []
    const participants = participantsData.map((p: any) => ({
      label: p[UserEntryFields.prog__display_name] || '',
      value: p[UserEntryFields.contacts__id] || '',
      email: p[UserEntryFields.contacts__email] || ''
    }))

    return {
      type: selectedItem?.[typeField] || 'note',
      datetime: datetime,
      subject: selectedItem?.[subjectField] || '',
      description: selectedItem?.[descriptionField] || '',
      owner: owner,
      participants: participants,
      notes: selectedItem?.[notesField] || ''
    }
  }

  const renderInteractionForm = () => (
    <InteractionForm
      ref={interactionFormRef}
      initialValues={getInteractionInitialValues()}
      interactionType={interactionType}
      onInteractionTypeChange={setInteractionType}
      isAddMode={isAddMode}
      className='detail-panel-form'
    />
  )

  const getHeaderTitle = (): string => {
    if (isFormMode) {
      if (isAddMode) {
        if (isTaskMode) {
          return 'New Task'
        } else {
          return 'New Interaction'
        }
      } else {
        if (isTaskMode) {
          return 'Edit Task'
        } else {
          return 'Edit Interaction'
        }
      }
    }

    if (itemType === 'task') {
      return 'Task Detail'
    } else if (itemType === 'interaction') {
      return 'Interaction Detail'
    }
    return 'Detail'
  }

  return (
    <div
      data-debug='detail-panel-container'
      data-component='DetailPanel'
      data-item-type={itemType}
      data-mode={mode}
      data-is-form-mode={isFormMode}
      data-is-add-mode={isAddMode}
      data-is-task-mode={isTaskMode}
      className='h-full'
    >
      <FormSection
        title={getHeaderTitle()}
        titleColor={isTaskMode ? 'green' : 'purple'}
        className='h-full'
        styles={{
          body: {
            height: 'calc(100% - 45px)',
            display: 'flex',
            flexDirection: 'column',
            padding: '8px',
            overflow: isFormMode ? 'auto' : 'hidden',
            minHeight: 0
          }
        }}
        headerActions={
          <>
            {isFormMode ? (
              <Space data-debug='detail-panel-actions' size='small'>
                <Tooltip title={isAddMode ? 'Add' : 'Update'}>
                  <Button
                    variant='save'
                    data-debug='detail-panel-submit-btn'
                    size='small'
                    icon={<FontAwesomeIcon icon={faCheck} style={{ color: '#FFFFFF' }} />}
                    onClick={handleSubmit}
                    loading={submitting}
                  />
                </Tooltip>
                <Tooltip title='Cancel'>
                  <Button
                    variant='cancel'
                    data-debug='detail-panel-cancel-btn'
                    size='small'
                    icon={<FontAwesomeIcon icon={faTimes} />}
                    onClick={handleCancel}
                    disabled={submitting}
                  />
                </Tooltip>
              </Space>
            ) : (
              <Space data-debug='detail-panel-actions' size='small'>
                <Tooltip title='Edit'>
                  <Button
                    variant='edit'
                    data-debug='detail-panel-edit-btn'
                    size='small'
                    icon={<FontAwesomeIcon icon={faEdit} />}
                    onClick={() => onEdit?.(selectedItem, itemType || 'task')}
                  />
                </Tooltip>
                {itemType === 'task' && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'link-delink',
                          label: (
                            <span>
                              <FontAwesomeIcon icon={faLink} style={{ marginRight: 8 }} />
                              Link/Delink
                            </span>
                          ),
                          onClick: () => setTaskLinkDelinkModalVisible(true)
                        },
                        {
                          key: 'delete',
                          label: (
                            <span>
                              <FontAwesomeIcon icon={faTrash} style={{ marginRight: 8, color: 'currentColor' }} />
                              Delete
                            </span>
                          ),
                          danger: true,
                          onClick: () => setTaskDeletePopconfirmVisible(true)
                        }
                      ]
                    }}
                    trigger={['click']}
                  >
                    <Popconfirm
                      title={CONFIRM_DELETE_TASK_TITLE}
                      description={
                        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '300px' }}>
                          {CONFIRM_DELETE_TASK_DESCRIPTION}
                        </div>
                      }
                      onConfirm={async () => {
                        setTaskDeletePopconfirmVisible(false)
                        await handleDeleteTask()
                      }}
                      onCancel={() => setTaskDeletePopconfirmVisible(false)}
                      okText='Delete'
                      cancelText='Cancel'
                      okButtonProps={{ danger: true }}
                      open={taskDeletePopconfirmVisible}
                      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                    >
                      <Tooltip title='More options'>
                        <Button
                          variant='default'
                          ref={taskEllipsesButtonRef}
                          data-debug='detail-panel-task-ellipses-btn'
                          size='small'
                          icon={<FontAwesomeIcon icon={faEllipsisV} />}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Dropdown>
                )}
                {itemType === 'interaction' && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'link-delink',
                          label: (
                            <span>
                              <FontAwesomeIcon icon={faLink} style={{ marginRight: 8 }} />
                              Link/Delink
                            </span>
                          ),
                          onClick: () => setLinkDelinkModalVisible(true)
                        },
                        {
                          key: 'delete',
                          label: (
                            <span>
                              <FontAwesomeIcon icon={faTrash} style={{ marginRight: 8, color: 'currentColor' }} />
                              Delete
                            </span>
                          ),
                          danger: true,
                          onClick: () => {
                            setDeletePopconfirmVisible(true)
                          }
                        }
                      ]
                    }}
                    trigger={['click']}
                  >
                    <Popconfirm
                      title={CONFIRM_DELETE_INTERACTION_TITLE}
                      description={
                        <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '300px' }}>
                          {CONFIRM_DELETE_INTERACTION_DESCRIPTION}
                        </div>
                      }
                      onConfirm={async () => {
                        setDeletePopconfirmVisible(false)
                        await handleDeleteInteraction()
                      }}
                      onCancel={() => setDeletePopconfirmVisible(false)}
                      okText='Delete'
                      cancelText='Cancel'
                      okButtonProps={{ danger: true }}
                      open={deletePopconfirmVisible}
                      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                    >
                      <Tooltip title='More options'>
                        <Button
                          variant='default'
                          ref={ellipsesButtonRef}
                          data-debug='detail-panel-ellipses-btn'
                          size='small'
                          icon={<FontAwesomeIcon icon={faEllipsisV} />}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </Dropdown>
                )}
                <Tooltip title='Close'>
                  <Button
                    variant='cancel'
                    data-debug='detail-panel-close-btn'
                    size='small'
                    icon={<FontAwesomeIcon icon={faTimes} />}
                    onClick={onClose}
                  />
                </Tooltip>
              </Space>
            )}
          </>
        }
      >
        <div
          data-debug='detail-panel-content'
          className='flex-1 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden'
        >
          {isFormMode ? (
            isTaskMode ? (
              <div data-debug='task-form-wrapper' className='min-h-0'>
                {renderTaskForm()}
              </div>
            ) : (
              <div data-debug='interaction-form-wrapper' className='min-h-0'>
                {renderInteractionForm()}
              </div>
            )
          ) : (
            selectedItem &&
            (itemType === 'task' ? (
              <div data-debug='task-detail-wrapper' className='h-full'>
                {renderTaskDetail(selectedItem)}
              </div>
            ) : (
              <div data-debug='interaction-detail-wrapper' className='h-full'>
                {renderInteractionDetail(selectedItem)}
              </div>
            ))
          )}
        </div>
      </FormSection>
      {itemType === 'interaction' && (
        <LinkDelinkModal
          visible={linkDelinkModalVisible}
          interaction={selectedItem}
          onCancel={() => setLinkDelinkModalVisible(false)}
          onSuccess={async () => {
            setLinkDelinkModalVisible(false)
            if (onRefreshInteractions) {
              await onRefreshInteractions()
            }
          }}
          accessToken={getAccessToken(store.getState().auth)}
          triggerRef={ellipsesButtonRef}
        />
      )}
      {itemType === 'task' && (
        <TaskLinkDelinkModal
          visible={taskLinkDelinkModalVisible}
          task={selectedItem}
          onCancel={() => setTaskLinkDelinkModalVisible(false)}
          onSuccess={async () => {
            setTaskLinkDelinkModalVisible(false)
            if (onRefreshTasks) {
              await onRefreshTasks()
            }
          }}
          accessToken={getAccessToken(store.getState().auth)}
          triggerRef={taskEllipsesButtonRef}
        />
      )}
    </div>
  )
}
